<template>
  <div class="map-wrapper">
    <div class="map-outer">
      <div class="indicator" v-if="initializing"><spinner /></div>
      <div class="map-inner" ref="mapContainer"></div>
    </div>

    <!-- Hover overlay slot -->
    <div v-if="hoveredItem && showHoverOverlay" class="hover-overlay">
      <slot
        name="hover-content"
        :item="hoveredItem"
        :location="hoveredLocation"
      >
        <!-- Default hover content -->
        <div class="default-hover-card">
          <h4>{{ hoveredItem.title || "Location" }}</h4>
          <p v-if="hoveredItem.description">{{ hoveredItem.description }}</p>
          <small
            >Lat: {{ hoveredItem.latitude }}, Lng:
            {{ hoveredItem.longitude }}</small
          >
        </div>
      </slot>
    </div>

    <!-- Custom controls slot -->
    <div v-if="$slots.controls" class="map-controls">
      <slot name="controls" :map="map" :markers="markers"></slot>
    </div>

    <!-- Legend or additional info slot -->
    <div v-if="$slots.legend" class="map-legend">
      <slot name="legend" :markers="markers"></slot>
    </div>
  </div>
</template>

<script>
import Spinner from "./spinner.vue";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

export default {
  components: {
    Spinner,
  },
  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      },
    },
    // Customization props
    mapOptions: {
      type: Object,
      default() {
        return {
          zoom: 12,
          streetViewControl: false,
        };
      },
    },
    markerOptions: {
      type: Object,
      default() {
        return {};
      },
    },
    circleOptions: {
      type: Object,
      default() {
        return {
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.35,
        };
      },
    },
    // Content props
    showInfoWindow: {
      type: Boolean,
      default: false,
    },
    infoWindowContent: {
      type: Function,
      default: null, // Function that receives location data and returns HTML string
    },
    // Event handling
    clickable: {
      type: Boolean,
      default: true,
    },
    // New hover functionality props
    showHoverOverlay: {
      type: Boolean,
      default: false,
    },
    hoverDelay: {
      type: Number,
      default: 300, // milliseconds
    },
    // Info window trigger options
    infoWindowTrigger: {
      type: String,
      default: "click", // 'click', 'hover', or 'both'
      validator: (value) => ["click", "hover", "both"].includes(value),
    },
    // Marker scattering options
    enableScattering: {
      type: Boolean,
      default: true,
    },
    scatterRadius: {
      type: Number,
      default: 0.00005, // Base radius in degrees (roughly 50-100 meters)
    },
    scatterRandomization: {
      type: Boolean,
      default: true, // Add randomization to avoid perfect circles
    },
    // Marker clustering options
    enableClustering: {
      type: Boolean,
      default: true, // Enabled by default for better performance with many markers
    },
    clusterOptions: {
      type: Object,
      default() {
        return {
          gridSize: 60, // Size of the grid for clustering
          maxZoom: 15, // Maximum zoom level at which clustering is enabled
          minimumClusterSize: 2, // Minimum number of markers to form a cluster
          averageCenter: true, // Whether the center of each cluster should be the average of all markers
          zoomOnClick: true, // Whether clicking a cluster should zoom the map
        };
      },
    },
    clusterStyles: {
      type: Array,
      default: null, // Use default Google Maps cluster styles
    },
  },
  emits: [
    "marker:click",
    "marker:hover",
    "marker:hover:enter",
    "marker:hover:leave",
    "map:click",
    "map:ready",
    "bounds:changed",
    "info-window:opened",
    "info-window:closed",
  ],
  data() {
    return {
      map: null,
      markers: [],
      markerClusterer: null, // MarkerClusterer instance
      infoWindow: null,
      model: this.modelValue,
      loadingMapScript: true,
      initialised: false,
      // Hover state
      hoveredItem: null,
      hoveredLocation: null,
      hoverTimeout: null,
      // Track info window state
      currentInfoWindowMarker: null,
      infoWindowOpenedBy: null, // 'click' or 'hover'
    };
  },
  computed: {
    initializing() {
      return this.loadingMapScript || !this.initialised;
    },
  },
  mounted() {
    const self = this;

    // Use the google maps api key set for the application or the built in qik.dev one as a backup
    const apiKey =
      self.$sdk.app?.global?.googleMapsAPIKey ||
      "AIzaSyDxBa2lg5dUZWvVSAO2EfgHHKuNnERMzec";

    // Ensure the maps are loaded
    self.$sdk.utils
      .loadExternalScript(
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}`,
      )
      .then(
        function (res) {
          self.loadingMapScript = false;
        },
        function (err) {
          self.loadingMapScript = false;
        },
      );
  },
  watch: {
    modelValue(m) {
      this.model = m;
      this.redrawMarkers();
    },
    loadingMapScript: { handler: "checkInitialisation", immediate: true },
  },
  beforeUnmount() {
    if (this.map) {
      window.google.maps.event.clearInstanceListeners(this.map);
    }
    // Clear hover timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    // Close info window
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  },
  methods: {
    checkInitialisation() {
      const self = this;
      if (self.loadingMapScript) {
      } else {
        // Initialize the map
        self.initializeMap();
      }
    },
    focus() {},

    // Deletes all markers in the array by removing references to them.
    deleteMarkers() {
      const self = this;

      // Close any open info window first
      if (self.infoWindow) {
        self.infoWindow.close();
        self.infoWindow = null;
      }

      // Clear hover state
      if (self.hoverTimeout) {
        clearTimeout(self.hoverTimeout);
        self.hoverTimeout = null;
      }
      self.hoveredItem = null;
      self.hoveredLocation = null;

      // Clear marker clusterer if it exists
      if (self.markerClusterer) {
        self.markerClusterer.clearMarkers();
        self.markerClusterer = null;
      }

      // Remove markers individually (fallback method)
      if (self.map && window.google && window.google.maps) {
        for (let i = 0; i < self.markers.length; i++) {
          const markerData = self.markers[i];
          if (!markerData) continue;

          const { marker, circle } = markerData;

          // Clear all event listeners and remove marker
          if (marker) {
            try {
              window.google.maps.event.clearInstanceListeners(marker);
              marker.setMap(null);
              marker.setVisible(false);
            } catch (e) {
              console.warn("Error removing marker:", e);
            }
          }

          // Clear circle if it exists
          if (circle) {
            try {
              window.google.maps.event.clearInstanceListeners(circle);
              circle.setMap(null);
              circle.setVisible(false);
            } catch (e) {
              console.warn("Error removing circle:", e);
            }
          }
        }
      }

      // Clear the markers array and reset state
      self.markers.length = 0;
      self.markers = [];
      self.currentInfoWindowMarker = null;
      self.infoWindowOpenedBy = null;
    },

    createMarkerIcon(location, index) {
      // Allow custom icon per location or global custom icon
      if (location.icon) {
        return location.icon;
      }
      if (this.markerOptions.icon) {
        return this.markerOptions.icon;
      }
      return null; // Use default Google Maps marker
    },

    createInfoWindowContent(location, index) {
      if (this.infoWindowContent) {
        return this.infoWindowContent(location, index);
      }

      // Check if we have a slot for info window content
      if (this.$slots["info-window"]) {
        try {
          // Create a temporary container to render the slot
          const tempDiv = document.createElement("div");

          // Get the slot content
          const slotContent = this.$slots["info-window"]({
            item: location,
            index: index,
            location: location,
          });

          if (slotContent && slotContent.length > 0) {
            // Create a temporary Vue app to render the slot content to HTML
            const { createApp, h } =
              this.$options._vueVersion === 3
                ? Vue
                : { createApp: null, h: null };

            if (createApp) {
              // Vue 3 approach
              const tempApp = createApp({
                render() {
                  return slotContent;
                },
              });
              tempApp.mount(tempDiv);
              const htmlString = tempDiv.innerHTML;
              tempApp.unmount();
              return htmlString;
            } else {
              // Vue 2 fallback - try to extract text content or use a simple approach
              const vnodeToString = (vnode) => {
                if (typeof vnode === "string") return vnode;
                if (typeof vnode === "number") return String(vnode);
                if (!vnode) return "";

                if (vnode.text) return vnode.text;
                if (vnode.children) {
                  return vnode.children.map(vnodeToString).join("");
                }
                if (vnode.tag) {
                  const attrs =
                    vnode.data && vnode.data.attrs
                      ? Object.entries(vnode.data.attrs)
                          .map(([key, value]) => `${key}="${value}"`)
                          .join(" ")
                      : "";
                  const attrsStr = attrs ? ` ${attrs}` : "";
                  const children = vnode.children
                    ? vnode.children.map(vnodeToString).join("")
                    : "";
                  return `<${vnode.tag}${attrsStr}>${children}</${vnode.tag}>`;
                }
                return "";
              };

              return slotContent.map(vnodeToString).join("");
            }
          }
        } catch (error) {
          console.warn("Error rendering info-window slot:", error);
          // Fall back to default content
        }
      }

      // Default info window content - compact styling to reduce whitespace
      return `
        <div style="min-width: 200px; margin: -8px -12px; padding: 12px;">
          <h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #333; line-height: 1.2;">${location.title || `Location ${index + 1}`}</h4>
          ${location.description ? `<p style="margin: 0 0 8px 0; font-size: 14px; color: #666; line-height: 1.3;">${location.description}</p>` : ""}
          <small style="color: #999; font-size: 12px;">Lat: ${location.latitude}, Lng: ${location.longitude}</small>
        </div>
      `;
    },

    openInfoWindow(location, index, marker, openedBy = "click") {
      const self = this;

      if (!self.infoWindow) {
        self.infoWindow = new google.maps.InfoWindow({
          pixelOffset: new google.maps.Size(0, -10),
          // Disable the default close button to reduce whitespace
          disableAutoPan: false,
          maxWidth: 300,
        });

        // Add close event listener
        self.infoWindow.addListener("closeclick", () => {
          self.currentInfoWindowMarker = null;
          self.infoWindowOpenedBy = null;
          self.$emit("info-window:closed");
        });
      }

      // Don't reopen if already open for this marker unless it's a different trigger
      if (
        self.currentInfoWindowMarker === index &&
        self.infoWindowOpenedBy === openedBy
      ) {
        return;
      }

      const content = self.createInfoWindowContent(location, index);
      self.infoWindow.setContent(content);
      self.infoWindow.open(self.map, marker);

      self.currentInfoWindowMarker = index;
      self.infoWindowOpenedBy = openedBy;

      self.$emit("info-window:opened", {
        location,
        index,
        marker,
        openedBy,
      });
    },

    closeInfoWindow() {
      if (this.infoWindow) {
        this.infoWindow.close();
        this.currentInfoWindowMarker = null;
        this.infoWindowOpenedBy = null;
      }
    },

    handleMarkerClick(location, index, marker) {
      this.$emit("marker:click", {
        ...location,
        marker: {
          index,
          marker,
          position: marker.getPosition(),
        },
      });

      // Show info window on click if enabled
      if (
        this.showInfoWindow &&
        (this.infoWindowTrigger === "click" ||
          this.infoWindowTrigger === "both")
      ) {
        // If info window is already open for this marker and opened by click, close it
        if (
          this.currentInfoWindowMarker === index &&
          this.infoWindowOpenedBy === "click"
        ) {
          this.closeInfoWindow();
        } else {
          this.openInfoWindow(location, index, marker, "click");
        }
      }
    },

    handleMarkerHover(location, index, marker, type) {
      const eventData = {
        ...location,
        marker: {
          index,
          marker,
          position: marker.getPosition(),
          type,
        },
      };

      // Emit specific hover events
      this.$emit("marker:hover", eventData);
      this.$emit(`marker:hover:${type}`, eventData);

      if (type === "enter") {
        // Clear any existing timeout
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
        }

        // Set hover state after delay
        this.hoverTimeout = setTimeout(() => {
          this.hoveredItem = location;
          this.hoveredLocation = location;

          // Show info window on hover if configured
          if (
            this.showInfoWindow &&
            (this.infoWindowTrigger === "hover" ||
              this.infoWindowTrigger === "both")
          ) {
            // Only open on hover if not already opened by click
            if (
              this.infoWindowOpenedBy !== "click" ||
              this.currentInfoWindowMarker !== index
            ) {
              this.openInfoWindow(location, index, marker, "hover");
            }
          }
        }, this.hoverDelay);
      } else if (type === "leave") {
        // Clear timeout and hover state
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
        }
        this.hoveredItem = null;
        this.hoveredLocation = null;

        // Close info window on hover leave only if it was opened by hover
        if (this.infoWindow && this.infoWindowOpenedBy === "hover") {
          // Add small delay to prevent flickering when moving mouse
          setTimeout(() => {
            if (this.infoWindowOpenedBy === "hover") {
              this.closeInfoWindow();
            }
          }, 100);
        }
      }
    },

    redrawMarkers() {
      const self = this;

      // Nuclear option: Completely recreate the map to ensure clean state
      if (self.map && self.markers.length > 0) {
        // Store current map state
        const currentCenter = self.map.getCenter();
        const currentZoom = self.map.getZoom();

        // Clear all event listeners from the map
        window.google.maps.event.clearInstanceListeners(self.map);

        // Destroy the current map completely
        self.map = null;

        // Recreate the map with the same options
        self.map = new window.google.maps.Map(self.$refs.mapContainer, {
          ...self.mapOptions,
          center: currentCenter,
          zoom: currentZoom,
        });

        // Re-add map click event listener
        self.map.addListener("click", (event) => {
          if (self.infoWindow) {
            self.closeInfoWindow();
          }
          self.$emit("map:click", {
            latLng: event.latLng,
            pixel: event.pixel,
          });
        });
      } else {
        // If no existing markers, just clear them normally
        self.deleteMarkers();
      }

      // Clear markers array and reset state
      self.markers = [];
      self.infoWindow = null;
      self.currentInfoWindowMarker = null;
      self.infoWindowOpenedBy = null;

      // Clear hover state
      if (self.hoverTimeout) {
        clearTimeout(self.hoverTimeout);
        self.hoverTimeout = null;
      }
      self.hoveredItem = null;
      self.hoveredLocation = null;

      // Process locations to handle overlapping coordinates
      const processedLocations = self.enableScattering
        ? self.scatterOverlappingLocations(self.model)
        : self.model.map((location) => ({
            location,
            adjustedLat: Number(location.latitude || 0),
            adjustedLng: Number(location.longitude || 0),
          }));

      const markersForClustering = [];

      processedLocations.forEach(function (locationData, index) {
        const { location, adjustedLat, adjustedLng } = locationData;
        const lat = Number(adjustedLat || location.latitude || 0);
        const lng = Number(adjustedLng || location.longitude || 0);

        if (!lat || !lng) {
          return;
        }

        const position = new google.maps.LatLng(lat, lng);

        // Merge custom marker options
        const markerConfig = {
          position,
          // Don't set map here if clustering is enabled - let clusterer handle it
          map: self.enableClustering ? null : self.map,
          ...self.markerOptions,
          ...(location.markerOptions || {}),
        };

        // Set custom icon if provided
        const icon = self.createMarkerIcon(location, index);
        if (icon) {
          markerConfig.icon = icon;
        }

        const marker = new window.google.maps.Marker(markerConfig);

        // Store location data on marker for clustering callbacks
        marker.locationData = location;
        marker.locationIndex = index;

        // Add click event listener
        if (self.clickable) {
          marker.addListener("click", () => {
            self.handleMarkerClick(location, index, marker);
          });
        }

        // Add hover events
        marker.addListener("mouseover", () => {
          self.handleMarkerHover(location, index, marker, "enter");
        });

        marker.addListener("mouseout", () => {
          self.handleMarkerHover(location, index, marker, "leave");
        });

        let circle;
        if (location.threshold) {
          // Use original coordinates for circle, not adjusted ones
          const originalPosition = new google.maps.LatLng(
            Number(location.latitude),
            Number(location.longitude),
          );

          // Merge custom circle options
          const circleConfig = {
            center: originalPosition,
            radius: Number(location.threshold),
            map: self.map,
            ...self.circleOptions,
            ...(location.circleOptions || {}),
          };

          circle = new window.google.maps.Circle(circleConfig);
        }

        self.markers.push({
          marker,
          circle,
          location,
          index,
        });

        // Add to clustering array if clustering is enabled
        if (self.enableClustering) {
          markersForClustering.push(marker);
        }
      });

      // Initialize clustering if enabled
      if (self.enableClustering && markersForClustering.length > 0) {
        self.initializeMarkerClustering(markersForClustering);
      }

      self.centralize();
    },

    scatterOverlappingLocations(locations) {
      const self = this;
      const processedLocations = [];
      const coordinateGroups = new Map();

      // Group locations by their coordinates
      locations.forEach((location, index) => {
        const lat = Number(location.latitude || 0);
        const lng = Number(location.longitude || 0);

        if (!lat || !lng) {
          processedLocations.push({
            location,
            adjustedLat: lat,
            adjustedLng: lng,
          });
          return;
        }

        const coordKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

        if (!coordinateGroups.has(coordKey)) {
          coordinateGroups.set(coordKey, []);
        }

        coordinateGroups.get(coordKey).push({ location, originalIndex: index });
      });

      // Process each coordinate group
      coordinateGroups.forEach((group, coordKey) => {
        if (group.length === 1) {
          // Single location, no scattering needed
          const { location } = group[0];
          processedLocations.push({
            location,
            adjustedLat: Number(location.latitude),
            adjustedLng: Number(location.longitude),
          });
        } else {
          // Multiple locations at same coordinates, scatter them
          const [baseLat, baseLng] = coordKey.split(",").map(Number);
          const scatteredPositions = self.generateScatteredPositions(
            baseLat,
            baseLng,
            group.length,
          );

          group.forEach((item, index) => {
            const { adjustedLat, adjustedLng } = scatteredPositions[index];
            processedLocations.push({
              location: item.location,
              adjustedLat,
              adjustedLng,
            });
          });
        }
      });

      return processedLocations;
    },

    generateScatteredPositions(baseLat, baseLng, count) {
      const positions = [];
      const baseRadius = this.scatterRadius;

      if (count === 1) {
        positions.push({ adjustedLat: baseLat, adjustedLng: baseLng });
        return positions;
      }

      // For 2 locations, place them on opposite sides
      if (count === 2) {
        const angle1 = 0;
        const angle2 = Math.PI;

        positions.push({
          adjustedLat: baseLat + baseRadius * Math.cos(angle1),
          adjustedLng: baseLng + baseRadius * Math.sin(angle1),
        });

        positions.push({
          adjustedLat: baseLat + baseRadius * Math.cos(angle2),
          adjustedLng: baseLng + baseRadius * Math.sin(angle2),
        });

        return positions;
      }

      // For 3+ locations, arrange in a circle with optional randomization
      for (let i = 0; i < count; i++) {
        // Base angle for even distribution
        const baseAngle = (2 * Math.PI * i) / count;

        // Add randomization if enabled
        let angle = baseAngle;
        let radius = baseRadius;

        if (this.scatterRandomization) {
          // Add some randomization to avoid perfect circles
          const angleVariation = (Math.random() - 0.5) * 0.5; // ±0.25 radians variation
          angle = baseAngle + angleVariation;

          // Vary the radius slightly for more natural distribution
          const radiusVariation = 0.5 + Math.random() * 0.5; // 0.5x to 1.0x of base radius
          radius = baseRadius * radiusVariation;
        }

        // For larger groups, use multiple rings
        const ringMultiplier = Math.floor(i / 8) + 1; // New ring every 8 markers
        const finalRadius = radius * ringMultiplier;

        positions.push({
          adjustedLat: baseLat + finalRadius * Math.cos(angle),
          adjustedLng: baseLng + finalRadius * Math.sin(angle),
        });
      }

      return positions;
    },

    centralize() {
      if (this.model.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      this.model.filter(Boolean).forEach((marker) => {
        bounds.extend(
          new google.maps.LatLng(marker.latitude, marker.longitude),
        );
      });
      this.map.fitBounds(bounds);

      this.$emit("bounds:changed", bounds);
    },

    initializeMap() {
      const self = this;
      if (self.initialised) {
        return;
      }

      self.initialised = true;

      // Setup the map with custom options
      self.map = new window.google.maps.Map(self.$refs.mapContainer, {
        ...self.mapOptions,
      });

      // Add map click event - close info window when clicking on map
      self.map.addListener("click", (event) => {
        // Close info window when clicking on map (not on marker)
        if (self.infoWindow) {
          self.closeInfoWindow();
        }

        self.$emit("map:click", {
          latLng: event.latLng,
          pixel: event.pixel,
        });
      });

      self.redrawMarkers();

      // Emit map ready event
      self.$emit("map:ready", self.map);
    },

    // Public methods for external control
    getMap() {
      return this.map;
    },

    getMarkers() {
      return this.markers;
    },

    focusMarker(index) {
      if (this.markers[index]) {
        const { marker } = this.markers[index];
        this.map.panTo(marker.getPosition());
        this.map.setZoom(15);
      }
    },

    openInfoWindowByIndex(index) {
      if (this.markers[index] && this.showInfoWindow) {
        const { marker, location } = this.markers[index];
        this.openInfoWindow(location, index, marker, "api");
      }
    },

    // Initialize marker clustering
    initializeMarkerClustering(markers) {
      const self = this;

      try {
        // Create clusterer options - use default styles unless custom ones are provided
        const clustererOptions = {
          map: self.map,
          markers: markers,
          ...self.clusterOptions,
        };

        // Only add custom renderer if custom styles are provided
        if (self.clusterStyles && self.clusterStyles.length > 0) {
          clustererOptions.renderer = {
            render: ({ count, position }, stats) => {
              // Determine which style to use based on marker count
              let styleIndex = 0;
              if (count >= 100) {
                styleIndex = Math.min(2, self.clusterStyles.length - 1);
              } else if (count >= 10) {
                styleIndex = Math.min(1, self.clusterStyles.length - 1);
              }

              const style = self.clusterStyles[styleIndex];

              // Get computed font family from the map container to inherit it
              const computedStyle = window.getComputedStyle(
                self.$refs.mapContainer,
              );
              const fontFamily =
                computedStyle.fontFamily || "Roboto, Arial, sans-serif";

              // Create cluster marker with inherited font family
              const svg = `
                <svg width="${style.width}" height="${style.height}" viewBox="0 0 ${style.width} ${style.height}" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="${style.width / 2}" cy="${style.height / 2}" r="${style.width / 2 - 2}" fill="#1976d2" stroke="white" stroke-width="2"/>
                  <text x="${style.width / 2}" y="${style.height / 2}" text-anchor="middle" dy="0.3em" fill="${style.textColor}" font-size="${style.textSize}" font-family="${fontFamily}" font-weight="500">${count}</text>
                </svg>
              `;

              const marker = new google.maps.Marker({
                position,
                icon: {
                  url: `data:image/svg+xml;base64,${btoa(svg)}`,
                  scaledSize: new google.maps.Size(style.width, style.height),
                  anchor: new google.maps.Point(
                    style.anchorIcon[0],
                    style.anchorIcon[1],
                  ),
                },
                zIndex: 1000,
              });

              return marker;
            },
          };
        }
        // If no custom styles provided, MarkerClusterer will use its default Google Maps styles

        // Create the marker clusterer
        self.markerClusterer = new MarkerClusterer(clustererOptions);
      } catch (error) {
        console.warn("Error initializing marker clustering:", error);
        // Fallback: add markers directly to map if clustering fails
        markers.forEach((marker) => {
          marker.setMap(self.map);
        });
      }
    },

    // Get the marker clusterer instance
    getMarkerClusterer() {
      return this.markerClusterer;
    },
  },
};
</script>

<style lang="scss">
.map-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.map-outer {
  overflow: hidden;
  height: 0;
  padding-bottom: 56.25%;
  position: relative;
  background: color-mix(in lab, currentColor 10%, transparent);
  flex: 1;

  .map-inner {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
  }

  .indicator {
    position: absolute;
    pointer-events: none;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    text-align: center;
    width: 100%;
    z-index: 8;
    font-size: 3em;
  }
}

.hover-overlay {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 300px;
  pointer-events: none; // Allow map interaction through overlay
}

.default-hover-card {
  padding: 16px;

  h4 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 16px;
  }

  p {
    margin: 0 0 8px 0;
    color: #666;
    font-size: 14px;
  }

  small {
    color: #999;
    font-size: 12px;
  }
}

.map-controls {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
}

.map-legend {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 12px;
}

a {
  cursor: pointer;
}

/* Custom info window styling to reduce whitespace */
:global(.gm-ui-hover-effect) {
  opacity: 0.6;
}

:global(.gm-ui-hover-effect:hover) {
  opacity: 1;
}

/* Reduce padding and whitespace in Google Maps info windows */
:global(.gm-style-iw) {
  padding: 0 !important;
}

:global(.gm-style-iw-d) {
  overflow: visible !important;
}

:global(.gm-style-iw-c) {
  padding: 0 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

:global(.gm-style-iw-t) {
  /* This is the close button container - we can style it */
  right: 8px !important;
  top: 8px !important;
}

:global(.gm-style-iw-tc) {
  /* Tail/pointer styling */
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1)) !important;
}
</style>
